import Price from './format-price';

/**
 * Calculation class used for all price calculations (lane, room high, sheeting)
 * @param {object} product - product data from the product page
 * @param {object} additionalData - additional data from the metaobject material_factor, manufacture_costs, surcharges and sheetings
 * @param {object} inputData - form data from the product page or line item properties
 * @returns {object} price calculation
 */
class PriceCalculation {
  constructor(productData, additionalData, inputData) {
    this.product = productData;
    this.additionalData = additionalData;
    this.inputData = inputData;

    this.options = {
      debug: false,
    };

    if (this.options.debug && !this.product) {
      console.log('No product data');
      return;
    }

    if (this.options.debug && !this.additionalData) {
      console.log('No additional data');
      return;
    }

    if (this.options.debug && !this.inputData) {
      console.log('No form data');
      return;
    }

    if (this.options.debug) console.log('Calculator', this);

    try {
      // Get product data that's is used often in the calculation
      this.productType = handleize(this.product.product_type);
      this.curtainType = handleize(this.product.curtain_type);
      this.fabricType = handleize(this.product.fabric_type);
      this.laneWidthInCm =
        this.fabricType == 'lanes' && this.product.lane_width_in_cm
          ? this.product.lane_width_in_cm
          : 140;

      // Get the input data
      this.inputWidth = this.inputData.width ? parseFloat(this.inputData.width) : 0;
      this.inputHeight = this.inputData.height ? parseFloat(this.inputData.height) : 0;
      this.pleating = this.inputData.pleating ? handleize(this.inputData.pleating) : 'double-pleat';
      this.partition = this.inputData.partition ? handleize(this.inputData.partition) : 'couple';
      this.sheetingType = this.inputData.sheeting_type
        ? handleize(this.inputData.sheeting_type)
        : 'none';
      this.sheetingId = this.inputData.sheeting_id ? parseInt(this.inputData.sheeting_id) : null;
      this.quantity = this.inputData.quantity ? parseInt(this.inputData.quantity) : 1;
      this.parts = this.partition == 'couple' ? 2 : 1;

      // Get the additional data
      this.factor = this.getFactor();
      this.surcharges = this.getSurcharges();
      this.manufactureCost = this.getManufactureCost();
      this.optionalSheetings = this.getOptionalSheetings(this.sheetingType);
      this.selectedSheeting = this.getSelectedSheeting(this.sheetingId, this.optionalSheetings);

      // Initialize the price class
      this.price = new Price(
        this.product.variant_id,
        this.product.from_price,
        this.product.from_compare_at_price,
        this.quantity
      );

      // Validate if min price should be use based on the smallest width / height and options
      const validateMinPrice = this.validateMinPrice();
      if (validateMinPrice?.price) {
        return validateMinPrice;
      }

      // Step 1 - Calculate the amount fabric width needed
      const fabricWidth = this.fabricWidth();

      // Step 2 - Calculate the amount fabric height needed
      const fabricHeight = this.fabricHeight();

      // Validate fabric width and height
      const validateFabricWidthAndHeight = this.validateFabricWidthAndHeight(
        fabricWidth,
        fabricHeight
      );
      if (validateFabricWidthAndHeight.errors.length >= 1) return validateFabricWidthAndHeight;

      // Step 3 - Calculate the amount lanes needed (only for lanes fabric)
      let numberOfLanes =
        this.fabricType == 'lanes'
          ? this.numberOfLanes(fabricWidth, this.laneWidthInCm)
          : {
              fabric: null,
              labor: null,
            };

      // Step 4 - Calculate the needed fabric in m2
      const neededFabricInMeters = this.neededFabricInMeters(
        fabricWidth,
        fabricHeight,
        numberOfLanes.fabric
      );

      // Step 5 - Calculate the fabric costs
      const fabricCosts = this.fabricCosts(
        neededFabricInMeters,
        this.product.price,
        this.product.compare_at_price
      );
      if (fabricCosts?.errors && fabricCosts?.errors.length >= 1) return fabricCosts;

      // Step 6 - Calculate the labor costs (use number of lanes or fabric width depending on fabric type)
      const laborCosts =
        this.fabricType == 'lanes'
          ? this.laborCosts(numberOfLanes.labor)
          : this.laborCosts(neededFabricInMeters);
      if (laborCosts?.errors && laborCosts?.errors.length >= 1) return laborCosts;

      // Step 7 - Calculate the surcharge costs
      const surchargeCosts = this.surchargeCosts(
        neededFabricInMeters,
        numberOfLanes.labor,
        fabricCosts,
        laborCosts
      );

      // Step 8 - Calculate the sheeting costs
      const sheetingCosts = this.sheetingCosts(
        fabricWidth,
        fabricHeight,
        numberOfLanes,
        neededFabricInMeters
      );
      if (sheetingCosts?.errors && sheetingCosts.errors.length >= 1) return sheetingCosts;

      // Step 9 - Calculate the total price
      const totalPrice = this.totalPrice(fabricCosts, laborCosts, surchargeCosts, sheetingCosts);

      // Use debug to log the price calculation
      if (this.options.debug) {
        console.log('Price calculation', {
          'fabric type': this.fabricType,
          'fabric width': fabricWidth,
          'fabric height': fabricHeight,
          'curtain type': this.curtainType,
          partition: this.partition,
          pleating: this.pleating,
          factor: this.factor.data.procent,
          'number of lanes': numberOfLanes,
          'lane width in cm': this.laneWidthInCm,
          'needed fabric in meters': neededFabricInMeters,
          'fabric costs': fabricCosts,
          'labor costs': laborCosts,
          'surcharge costs': surchargeCosts,
          'sheeting costs': sheetingCosts,
          'total price': totalPrice,
        });
      }

      // Return the total price
      return totalPrice;
    } catch (error) {
      console.error(error);
      return {
        ...this.price.format(this.product.from_price, this.product.from_compare_at_price),
        errors: [
          {
            type: 'general',
            handle: 'something_went_wrong',
            message: `Sorry, something went wrong. Please try again.`,
          },
        ],
      };
    }
  }

  /**
   * Get the factor based of curtain type, fabric type and pleating
   * @param {string} curtainType
   * @param {string} fabricType
   * @param {string} pleating
   * @returns {object} factor
   */
  getFactor(
    curtainType = this.curtainType,
    fabricType = this.fabricType,
    pleating = this.pleating
  ) {
    return this.additionalData.factors.find((factor) => {
      const hasCurtainType = factor.data['curtain_type'].find(
        (type) => handleize(type) == curtainType
      );
      const hasFabricType = factor.data['fabric_type'].find(
        (type) => handleize(type) == fabricType
      );
      const hasPleating = factor.data['pleating_type'].find((type) => handleize(type) == pleating);
      if (hasCurtainType && hasFabricType && hasPleating) {
        return factor;
      }
    });
  }

  /**
   * Get the manufacture costs based of curtain type, fabric type and pleating
   * @param {string} curtainType
   * @param {string} fabricType
   * @param {string} pleatingOrSheeting
   * @param {number} laneWidthInCm
   * @returns {object} manufacture costs
   */
  getManufactureCost(
    curtainType = this.curtainType,
    fabricType = this.fabricType,
    pleatingOrSheeting = this.pleating,
    laneWidthInCm = this.laneWidthInCm
  ) {
    return this.additionalData.manufactures.find((cost) => {
      const hasCurtainType = cost.data['curtain_type'].find(
        (type) => handleize(type) == curtainType
      );
      const hasFabricType = cost.data['fabric_type'].find((type) => handleize(type) == fabricType);
      const hasPleatingOrSheeting = cost.data['pleating_type'].find(
        (type) => handleize(type) == pleatingOrSheeting
      );
      let matchSize = true;
      if (laneWidthInCm && cost.data['min_width'] >= laneWidthInCm) {
        matchSize = false;
      } else if (laneWidthInCm && cost.data['max_width'] <= laneWidthInCm) {
        matchSize = false;
      }
      if (hasCurtainType && hasFabricType && hasPleatingOrSheeting && matchSize) {
        return cost;
      }
    });
  }

  /**
   * Get the surcharge based of curtain type, fabric type and pleating
   * @param {string} curtainType
   * @param {string} fabricType
   * @param {string} pleating
   * @returns {array} with surcharges objects
   */
  getSurcharges(
    productType = this.productType,
    curtainType = this.curtainType,
    fabricType = this.fabricType,
    pleating = this.pleating
  ) {
    return this.additionalData.surcharges.filter((surcharge) => {
      // Check if the product type is in the surcharge
      if (!surcharge.data['product_type']) return false;
      const isProductType = surcharge.data['product_type'].find((type) => {
        return handleize(type) == productType;
      });
      if (!isProductType) return false;

      // Check if the curtain is the same
      const hasCurtainType = surcharge.data['curtain_type']
        ? surcharge.data['curtain_type'].find((type) => handleize(type) == curtainType)
        : false;

      // Check if the fabric is the same
      const hasFabricType = surcharge.data['fabric_type']
        ? surcharge.data['fabric_type'].find((type) => handleize(type) == fabricType)
        : false;

      // Check if the pleating is the same
      const hasPleating = surcharge.data['pleating_type']
        ? surcharge.data['pleating_type'].find((type) => handleize(type) == pleating)
        : false;

      // Return the surcharge if all the conditions are met
      if (hasCurtainType && hasFabricType && hasPleating) {
        return surcharge;
      }
    });
  }

  /**
   * Get the sheeting data based ot the form data
   * @param {string} type - sheeting type
   */
  getOptionalSheetings(type, id) {
    if (!type || type == 'none') {
      return [];
    }
    return this.additionalData.sheetings.filter((sheeting) => {
      const sheetingType = sheeting.data['type'] ? handleize(sheeting.data['type']) : null;
      if (!sheetingType) {
        return false;
      }
      return sheetingType == type;
    });
  }

  /**
   * Get the sheeting data based ot the form data
   * @param {number} id - sheeting id
   * @returns {object} sheeting data
   */
  getSelectedSheeting(id, sheetings) {
    if (!id) {
      return;
    }
    return sheetings.find((sheeting) => {
      return parseInt(sheeting.id) == id;
    });
  }

  /**
   * Validate if min price should be use based on the smallest width / height and options
   */
  validateMinPrice() {
    if (
      this.inputWidth == this.product['min_width'] &&
      this.inputHeight == this.product['min_height'] &&
      this.pleating == 'single-pleat' &&
      this.parts == 1 &&
      this.sheetingType == 'none'
    ) {
      return {
        ...this.price.format(this.product.from_price, this.product.from_compare_at_price),
      };
    } else {
      return;
    }
  }

  /**
   * Validate if fabric width and height are valid
   */
  validateFabricWidthAndHeight(fabricWidth, fabricHeight) {
    const errors = [];
    if (
      !this.inputWidth ||
      this.inputWidth < this.product['min_width'] ||
      this.inputWidth > this.product['max_width']
    ) {
      errors.push({
        type: 'width',
        handle: 'invalid_width',
        message: `Width should be in between ${this.product['min_width']} cm and ${this.product['max_width']} cm`,
      });
    }
    if (
      !this.inputHeight ||
      this.inputHeight < this.product['min_height'] ||
      this.inputHeight > this.product['max_height']
    ) {
      errors.push({
        type: 'height',
        handle: 'invalid_height',
        message: `Height should be in between ${this.product['min_height']} cm and ${this.product['max_height']} cm`,
      });
    }

    if (
      this.product['max_square_centimeter'] > 0 &&
      fabricWidth * fabricHeight > this.product['max_square_centimeter']
    ) {
      errors.push({
        type: 'general',
        handle: 'max_square_meter',
        message: `The maximum m2 is ${this.product['max_square_centimeter'] / 10000} m2`,
      });
    }
    return {
      ...this.price.format(this.product.from_price, this.product.from_compare_at_price),
      errors: errors,
    };
  }

  /**
   * Step 1 - Calculate the amount fabric width needed
   * Required metadata: Material factor
   * (Input width x Fabric factor) + Surcharge in CM per partition (side hems) = Fabric width needed
   * @returns {number} fabric width
   *
   */
  fabricWidth() {
    const surchargeInCm =
      this.factor && this.factor.data['sidehems_in_cm_per_partition']
        ? this.factor.data['sidehems_in_cm_per_partition'] * this.parts
        : 0;
    const factor = this.factor.data['procent'] ? this.factor.data['procent'] : 180;
    if (!this.inputWidth) {
      return;
    }

    return Math.ceil((this.inputWidth * factor) / 100) + surchargeInCm;
  }

  /**
   * Step 2 - Calculate the amount fabric height needed
   * Required metadata: Factors
   * input height + bottom hem surcharge from factors  = fabric height needed
   * @returns {number} fabric height
   */
  fabricHeight() {
    const surchargeInCm = this.factor.data['bottomhem_in_cm']
      ? this.factor.data['bottomhem_in_cm']
      : 0;
    if (!this.inputHeight) {
      return;
    }

    return Math.ceil(this.inputHeight) + surchargeInCm;
  }

  /**
   * Step 3 - Calculate the amount lanes needed
   * Required metadata: Factors and current product lane width
   * Math.round(fabric width / lane width) = number of lanes
   * Control calculation: (number of lanes x lane width) / input width = control factor
   * if control factor is lower than the fabric factor - 10%, add 1 lane
   * Note the number of lanes is different for fabric and labor
   * @param {number} fabricWidth
   * @param {number} laneWidthInCm
   * @returns {object} number of lanes for fabric and labor
   */
  numberOfLanes(fabricWidth, laneWidthInCm = this.laneWidthInCm) {
    const factor = this.factor.data['procent'] ? this.factor.data['procent'] : 180;
    let numberOfLanes = Math.round(fabricWidth / laneWidthInCm);
    const controlFactor = (numberOfLanes * laneWidthInCm) / this.inputWidth;
    if (controlFactor < (factor / 100) * 0.9) {
      numberOfLanes += 1;
    }
    if (numberOfLanes <= 0) {
      numberOfLanes += 1;
    }
    // This only applies to labor costs
    // If a couple curtain and factor is a odd number add 1 lane since the curtain will be split in half
    let numberOfLanesLabor = numberOfLanes;
    if (this.parts != 1 && numberOfLanesLabor % 2 != 0) {
      numberOfLanesLabor += 1;
    }
    return {
      fabric: numberOfLanes,
      labor: numberOfLanesLabor,
    };
  }

  /**
   * Step 4 - Calculate the needed fabric in m2
   * Number of lanes * fabric height = needed fabric fabric
   * @param {number} fabricWidth
   * @param {number} fabricHeight
   * @param {number} numberOfLanes - optional
   * @param {string} fabricType - optional
   * @returns {number} needed fabric in meters
   */
  neededFabricInMeters(fabricWidth, fabricHeight, numberOfLanes, fabricType = this.fabricType) {
    if (fabricType == 'lanes' && numberOfLanes) {
      return Math.round(numberOfLanes * fabricHeight) / 100;
    }
    return fabricWidth / 100;
  }

  /**
   * Step 5 - Calculate the fabric costs
   * Required metadata: Fabric costs in cents
   * Needed fabric width * meter price = fabric costs in cents
   * Needed fabric width * meter compare at price = fabric compare at costs
   * @param {number} width - number of lanes or fabric width in meter
   * @param {number} meterPrice - optional
   * @param {number} meterCompareAtPrice - optional
   * @returns {object} fabric costs with @price and @compare_at_price
   */
  fabricCosts(width, meterPrice = this.product.price, meterCompareAtPrice) {
    if (!width || !meterPrice) {
      return {
        ...this.price.format(this.product.from_price, this.product.from_compare_at_price),
        errors: [
          {
            type: 'general',
            handle: 'product_has_no_price',
            message: `Product has no price per meter or lane.`,
          },
        ],
      };
    }

    const price = Math.round(width * meterPrice);
    const compareAtPrice = meterCompareAtPrice ? Math.round(width * meterCompareAtPrice) : price;

    return {
      price: price,
      compare_at_price: compareAtPrice,
    };
  }

  /**
   * Step 6 - Calculate the labor costs
   * Required metadata: Manufacture costs
   * Fabric width or number of lanes x labor costs
   * @param {number} width - number of lanes or fabric width in meter
   * @param {number} laborCosts - amount of labor costs per l
   * @returns {number} labor costs in cents
   */
  laborCosts(width, laborCosts = this.manufactureCost.data['labor_costs']) {
    if (!width || !laborCosts || laborCosts <= 0) {
      return {
        ...this.price.format(this.product.from_price, this.product.from_compare_at_price),
        errors: [
          {
            type: 'general',
            handle: 'product_has_no_labor_costs',
            message: `Product has no labor costs`,
          },
        ],
      };
    }
    if (!width) {
      return;
    }
    return Math.round(width * laborCosts);
  }

  /**
   * Step 7  Calculate the surcharge costs
   * Required metadata: Surcharge costs
   * @param {number} neededFabricInMeters - optional
   * @param {number} numberOfLanes - optional
   * @param {object} fabricCosts - object with @price and @compare_at_price
   * @param {number} laborCosts - labor costs in cents
   * @returns {number} surcharge costs in cents
   */
  surchargeCosts(neededFabricInMeters = 1, numberOfLanes = 1, fabricCosts, laborCosts) {
    return this.surcharges.reduce(
      (costs, surcharge) => {
        let updated = false;
        if (this.fabricType == 'lanes' && surcharge.data['price_per_lane']) {
          const lanePrice = parseInt(surcharge.data['price_per_lane']);
          costs.price = costs.price + lanePrice * numberOfLanes;
          costs.compare_at_price = costs.compare_at_price + lanePrice * numberOfLanes;
          updated = true;
        } else if (this.fabricType == 'room-high' && surcharge.data['price_per_meter']) {
          const meterPrice = parseInt(surcharge.data['price_per_meter']);
          costs.price = costs.price + meterPrice * neededFabricInMeters;
          costs.compare_at_price = costs.compare_at_price + meterPrice * neededFabricInMeters;
          updated = true;
        }
        if (!updated && surcharge.data['percentage']) {
          const currentCosts = fabricCosts.price + laborCosts;
          const currentCompareAtCosts = fabricCosts.compare_at_price + laborCosts;
          costs.price =
            costs.price + Math.round((currentCosts / 100) * parseInt(surcharge.data['percentage']));
          costs.compare_at_price = Math.round(
            costs.compare_at_price +
              (currentCompareAtCosts / 100) * parseInt(surcharge.data['percentage'])
          );
          updated = true;
        }
        if (!updated && surcharge.data['price_per_piece']) {
          const piecesPerPartition = surcharge.data['pieces_per_partition']
            ? surcharge.data['pieces_per_partition']
            : 1;
          const pieces = piecesPerPartition * this.parts;
          const piecesPrice = parseInt(surcharge.data['price_per_piece']) * pieces;
          costs.price = costs.price + piecesPrice;
          costs.compare_at_price = costs.compare_at_price + piecesPrice;
          updated = true;
        }
        return costs;
      },
      {
        price: 0,
        compare_at_price: 0,
      }
    );
  }

  /**
   * Step 8  Calculate the sheeting costs
   * Required metadata: Sheetings and Manufacture costs
   * Needed fabric width * sheeting meter price = fabric costs in cents
   * Needed fabric width * sheeting meter compare at price = fabric compare at costs
   * Fabric width or number of lanes x sheeting labor costs
   * There are no surcharge costs for sheeting
   * @param {number} fabricWidth - Fabric width curtain in the front
   * @param {number} fabricHeight - Fabric height curtain in the front
   * @returns {object} numberOfLanesCurtainInFront - nunber of lanes for fabric and labor curtain in the front
   * @param {number} neededFabricInMetersCurtainInFront - needed fabric in meters curtain in the front
   */
  sheetingCosts(
    fabricWidth,
    fabricHeight,
    numberOfLanesCurtainInFront,
    neededFabricInMetersCurtainInFront
  ) {
    if (!this.selectedSheeting) {
      return {
        price: 0,
        compare_at_price: 0,
      };
    }

    // Get needed data
    const curtainType = this.curtainType;
    const fabricType = handleize(this.selectedSheeting.data['fabric_type']);
    const pleatingType = 'sheeting';
    const laneWidthInCm =
      fabricType == 'lanes' && this.selectedSheeting.data['width']
        ? this.selectedSheeting.data['width']
        : 140;
    const manufactureCost = this.getManufactureCost(
      curtainType,
      fabricType,
      pleatingType,
      laneWidthInCm
    );
    const errors = [];

    // Step 8.1 Calculate the amount lanes needed (not needed for room high)
    let numberOfLanes =
      fabricType == 'lanes'
        ? this.numberOfLanes(fabricWidth, laneWidthInCm)
        : {
            fabric: null,
            labor: null,
          };

    // Step 8.2 Calculate the needed fabric in meters
    const neededFabricInMeters = this.neededFabricInMeters(
      fabricWidth,
      fabricHeight,
      numberOfLanes.fabric,
      fabricType
    );

    // Step 8.3 Calculate the fabric costs
    const fabricCosts = this.fabricCosts(
      neededFabricInMeters,
      this.selectedSheeting.data['price'],
      this.selectedSheeting.data['compare_at_price']
    );
    if (fabricCosts?.errors && fabricCosts?.errors.length >= 1) {
      fabricCosts.errors[0].message = 'Sheeting has no price per meter or lane.';
      errors.push(fabricCosts.errors[0]);
    }

    // Step 8.4 Calculate the labor costs based on the curtain in front
    const laborCostsPerMeter = manufactureCost.data['labor_costs']
      ? parseInt(manufactureCost.data['labor_costs'])
      : 0;
    const laborCosts =
      this.fabricType == 'lanes'
        ? this.laborCosts(numberOfLanesCurtainInFront.labor, laborCostsPerMeter)
        : this.laborCosts(neededFabricInMetersCurtainInFront, laborCostsPerMeter);
    if (laborCosts?.errors && laborCosts?.errors.length >= 1) {
      laborCosts.errors[0].message = 'Sheeting has no labor costs';
      errors.push(laborCosts.errors[0]);
    }

    // Step 8.5 Calculate and return the total price
    return {
      price: fabricCosts.price + laborCosts,
      compare_at_price: fabricCosts.compare_at_price + laborCosts,
      sheeting: {
        'fabric type': fabricType,
        'fabric type for labor': this.fabricType,
        'fabric width curtain in front': fabricWidth,
        'fabric height curtain in front': fabricHeight,
        'number of lanes': numberOfLanes,
        'number of lanes for labor': numberOfLanesCurtainInFront,
        'needed fabric in meters': neededFabricInMeters,
        'needed fabric in meters for labor': neededFabricInMetersCurtainInFront,
        'fabric costs': fabricCosts,
        'labor costs': laborCosts,
      },
      errors: errors,
    };
  }

  /**
   * Step 9  - Calculate the total price
   * Required metadata: Factors
   * Fabric costs + labor costs + surcharge costs + sheeting costs = total price
   * @param {object} fabricCosts - object with @price and @compare_at_price
   * @param {number} laborCosts
   * @param {number} surchargeCosts
   * @param {number} sheetingCosts
   * @returns {object} price with @price and @compare_at_price
   */
  totalPrice(fabricCosts, laborCosts, surchargeCosts, sheetingCosts) {
    const price = fabricCosts.price + laborCosts + surchargeCosts.price + sheetingCosts.price;
    const compareAtPrice =
      fabricCosts.compare_at_price +
      laborCosts +
      surchargeCosts.compare_at_price +
      sheetingCosts.compare_at_price;

    // Price can't be higher then 9.999.999,99 because of token generation
    if (price > 999999999 || compareAtPrice > 999999999) {
      return {
        ...this.price.format(this.product.max_price, this.product.max_price),
        errors: [
          {
            type: 'general',
            handle: 'price_to_high',
            message: `The price is to high. Please contact us for a quote.`,
          },
        ],
      };
    }

    return this.price.format(price, compareAtPrice);
  }
}

export default PriceCalculation;
